import { useEffect, useState, type FC } from 'react';
import classes from './LoginModal.module.scss';
import { Button, Form, Input, message, Modal, Spin } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import { useLogInMutation } from '../../redux/API/ambassadorSlice';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { login } from '../../redux/isAuthSlice';

interface LoginModalProps {
  isModalOpen: boolean;
  setIsModalOpen: Function;
}

const LoginModal: FC<LoginModalProps> = ({ isModalOpen = false, setIsModalOpen = () => {} }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form] = useForm();
  const values = useWatch([], form);
  const [isDisabledBtn, setIsDisabledBtn] = useState<boolean>(true);
  const [logIn, { isLoading, isError }] = useLogInMutation();

  const initialValues = {
    email: '',
    password: ''
  };

  const onCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleBtnDisable = () => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsDisabledBtn(false))
      .catch(() => setIsDisabledBtn(true));
  };

  const onFinish = async () => {
    setIsDisabledBtn(true);

    try {
      const result = await logIn({ email: values.email, password: values.password }).unwrap();

      if (result?.user?.username) {
        dispatch(login({ isAuth: result.jwt, username: result.user?.username }));
        form.resetFields();
        message.success({ content: `${t('greeting')}, ${result?.user?.username}`, duration: 3 });
        navigate('/profile');
        setIsModalOpen(false);
      }
    } catch (err) {
      message.error({ content: t('authError'), duration: 3 });
      setIsDisabledBtn(false);
    }
  };

  useEffect(() => {
    handleBtnDisable();
  }, [form, values]);

  return (
    <Modal
      title={t('authorize')}
      closable={true}
      open={isModalOpen}
      confirmLoading={isLoading}
      onCancel={() => onCancel()}
      centered
      footer={null}
      className={classes.login}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className={classes.login__form}
        onFinish={onFinish}
        initialValues={initialValues}
      >
        <Form.Item
          name="email"
          rules={[
            {
              required: true,
              type: 'email',
              message: ''
            }
          ]}
        >
          <Input placeholder={t('emailRequired')} className={classes.login__form__input} />
        </Form.Item>

        <Form.Item
          name="password"
          rules={[
            {
              required: true,
              message: ''
            }
          ]}
        >
          <Input.Password placeholder={t('passwordRequired')} className={classes.login__form__input} />
        </Form.Item>

        <Button type="primary" htmlType="submit" className={classes.login__form__btn} disabled={isDisabledBtn}>
          {isLoading && <Spin size="default" />}
          {t('send')}
        </Button>
      </Form>
    </Modal>
  );
};

export default LoginModal;
